import { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { useQuery, ApolloClient } from '@apollo/client';
import { useLocale } from '../../../../../contexts/LocaleProvider';
import {
    GET_GEOGRAPHIC_REGIONS_QUERY,
    getGeographicRegionsQueryVariables,
} from '../queries/getGeographicRegionsQuery';
import { GeographicRegion } from '../types/GeographicRegion';
import { Country } from '../types/Country';
import { SubnationalRegion } from '../types/SubnationalRegion';
import { transformGeographicRegionResponse } from '../tools/transformGeographicRegionResponse';
import { useQueryFallbackResponse } from '../../../common/hooks/useQueryFallbackResponse';

type ByIdMap<T> = Record<string, T>;

interface GeographicRegionsContextValue {
    geographicRegions: GeographicRegion[];
    countries: Country[];
    subnationalRegions: SubnationalRegion[];
    geographicRegionById: ByIdMap<GeographicRegion>;
    countryById: ByIdMap<Country>;
    subnationalRegionById: ByIdMap<SubnationalRegion>;
    loading: boolean;
    error: any;
}

const GeographicRegionsContext = createContext<GeographicRegionsContextValue | undefined>(
    undefined
);

interface GeographicRegionsProviderProps {
    children: ReactNode;
    apolloClient: ApolloClient<any>;
}

const GeographicRegionsProvider = ({ children, apolloClient }: GeographicRegionsProviderProps) => {
    const { locale } = useLocale();
    const [geographicRegions, setGeographicRegions] = useState<GeographicRegion[]>([]);
    const [countries, setCountries] = useState<Country[]>([]);
    const [subnationalRegions, setSubnationalRegions] = useState<SubnationalRegion[]>([]);
    const [geographicRegionById, setGeographicRegionById] = useState<ByIdMap<GeographicRegion>>({});
    const [countryById, setCountryById] = useState<ByIdMap<Country>>({});
    const [subnationalRegionById, setSubnationalRegionById] = useState<ByIdMap<SubnationalRegion>>(
        {}
    );

    const query = GET_GEOGRAPHIC_REGIONS_QUERY;
    const {
        loading: loadingQuery,
        error: queryError,
        data: queryData,
    } = useQuery(query, {
        variables: getGeographicRegionsQueryVariables(locale),
        client: apolloClient,
    });

    const {
        fallbackData,
        loading: loadingFallback,
        error: fallbackError,
    } = useQueryFallbackResponse({ query, locale, initialized: true, queryError: queryError });

    useEffect(() => {
        const data = queryData ?? fallbackData;
        if (!data) return;
        const { geographicRegions, countries, subnationalRegions } =
            transformGeographicRegionResponse(data);

        const regionMap: ByIdMap<GeographicRegion> = {};
        geographicRegions.forEach((region) => {
            regionMap[region.id] = region;
        });

        const countryMap: ByIdMap<Country> = {};
        countries.forEach((country) => {
            countryMap[country.id] = country;
        });

        const subRegionMap: ByIdMap<SubnationalRegion> = {};
        subnationalRegions.forEach((subregion) => {
            subRegionMap[subregion.id] = subregion;
        });

        setGeographicRegions(geographicRegions);
        setCountries(countries);
        setSubnationalRegions(subnationalRegions);
        setGeographicRegionById(regionMap);
        setCountryById(countryMap);
        setSubnationalRegionById(subRegionMap);
    }, [queryData, fallbackData]);

    const loading = loadingQuery || loadingFallback;
    // if we have fallback data, we don't consider it an error
    const error = fallbackError || (queryError && !fallbackData ? queryError : null);

    return (
        <GeographicRegionsContext.Provider
            value={{
                geographicRegions,
                countries,
                subnationalRegions,
                geographicRegionById,
                countryById,
                subnationalRegionById,
                loading,
                error,
            }}
        >
            {children}
        </GeographicRegionsContext.Provider>
    );
};

function useGeographicRegions() {
    const context = useContext(GeographicRegionsContext);
    if (!context) {
        throw new Error('useGeographicRegions must be used within a GeographicRegionsProvider');
    }
    return context;
}

export { GeographicRegionsProvider, useGeographicRegions };
export type { GeographicRegionsContextValue };

// Export specific types instead of generic ByIdMap<T>
export type GeographicRegionById = ByIdMap<GeographicRegion>;
export type CountryById = ByIdMap<Country>;
export type SubnationalRegionById = ByIdMap<SubnationalRegion>;
